import {omneticApi} from '@dms/api/base';

import {buildBulkOperationsUrl} from './buildBulkOperationsUrl';
import {
  BulkOperationItemsResponseBodySchema,
  BulkUserOptionSchema,
  type BulkUserOption,
  BulkOperationResponseBodySchema,
  RerunFailedBulkOperationResponseBodySchema,
  type RerunFailedBulkOperationResponseBody,
  SubmitBulkOperationResponseBodySchema,
  SubmitSelectedRecordsBulkOperationRequestBodySchema,
  type BulkOperationItem,
  type BulkOperationResponseBody,
  type GetBulkOperationApiArg,
  type ListBulkOperationItemsApiArg,
  type ListBulkOperationsApiArg,
  type RerunFailedBulkOperationApiArg,
  type SubmitBulkOperationResponseBody,
  type SubmitSelectedRecordsBulkOperationApiArg,
} from './types';

// Operation writes enqueue asynchronous work; their response is not a new operation snapshot.
// useBulkOperationTracking registers submitted runs and reconciles AppSync progress, then forces
// detail refreshes on terminal pushes, reconnect, and tab visibility. List queries refetch on
// mount. Keep that refresh owner instead of mutation tags that replay stale pre-processing data.
export const bulkOperationApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    listBulkUserOptions: build.query<BulkUserOption[], {search: string}>({
      query: (params) => ({
        url: '/dms/v1/participation/bulk-user-option',
        params: {search: params.search.trim() || undefined},
      }),
      extraOptions: {responseSchema: BulkUserOptionSchema.array()},
    }),
    submitSelectedRecordsBulkOperation: build.mutation<
      SubmitBulkOperationResponseBody,
      SubmitSelectedRecordsBulkOperationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/resource/${encodeURIComponent(queryArg.resourceId)}/action/${encodeURIComponent(queryArg.actionCode)}`,
        method: 'POST',
        body: queryArg.body,
      }),
      extraOptions: {
        requestSchema: SubmitSelectedRecordsBulkOperationRequestBodySchema,
        responseSchema: SubmitBulkOperationResponseBodySchema,
      },
    }),
    listBulkOperations: build.query<BulkOperationResponseBody[], ListBulkOperationsApiArg>({
      query: (queryArg) => ({url: buildBulkOperationsUrl(queryArg)}),
      extraOptions: {responseSchema: BulkOperationResponseBodySchema.array()},
    }),
    getBulkOperation: build.query<BulkOperationResponseBody, GetBulkOperationApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${encodeURIComponent(queryArg.bulkOperationId)}`,
      }),
      extraOptions: {responseSchema: BulkOperationResponseBodySchema},
    }),
    listBulkOperationItems: build.query<BulkOperationItem[], ListBulkOperationItemsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${encodeURIComponent(queryArg.bulkOperationId)}/item`,
        params: {
          state: queryArg.state,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
      extraOptions: {responseSchema: BulkOperationItemsResponseBodySchema},
    }),
    rerunFailedBulkOperation: build.mutation<
      RerunFailedBulkOperationResponseBody,
      RerunFailedBulkOperationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${encodeURIComponent(queryArg.bulkOperationId)}/rerun-failed`,
        method: 'POST',
      }),
      extraOptions: {responseSchema: RerunFailedBulkOperationResponseBodySchema},
    }),
  }),
});

export const {
  useLazyListBulkUserOptionsQuery,
  useGetBulkOperationQuery,
  useLazyGetBulkOperationQuery,
  useListBulkOperationItemsQuery,
  useLazyListBulkOperationItemsQuery,
  useListBulkOperationsQuery,
  useRerunFailedBulkOperationMutation,
  useSubmitSelectedRecordsBulkOperationMutation,
} = bulkOperationApi;
